import React, { useEffect, useState } from 'react';
import { sessionInstance } from '~/utils';
import { Card, CardBody } from 'reactstrap';
import { useSession as useAuth, setAccessToken, Form, Field, Button } from '@cordata/buffet';
import { find } from 'lodash';
import { useField, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

const UserField = ({
  getOptionLabel,
  getOptionValue,
  name,
  ...rest
}) => {
  const props = {
    getOptionLabel,
    getOptionValue,
    name,
    ...rest,
  };

  const { values, setFieldValue } = useFormikContext();
  const [field] = useField(props);
  const [users, setUsers] = useState([]);
  const { division } = values;

  useEffect(() => {
    if (division && division.id) {
      sessionInstance.get(`division/${division.id}/users`, { params: { 'can-impersonate': true } })
        .then((r) => {
          setUsers(r.data.data);
        }).catch((e) => {
          setUsers(undefined);
          setFieldValue('error', e.response.data.message);
        });
    } else {
      setUsers(undefined);
    }
  }, [division]);

  useEffect(() => {
    if (users && users.length === 1) {
      setFieldValue(name, users[0]);
    }
  }, [users]);

  const UserSelect = () => (
    <Field
      {...props}
      {...field}
      type="select"
      options={users}
    />
  );

  const UserLabel = () => (
    <>
      <div>User: {getOptionLabel(users[0])}</div>

      <Field
        {...props}
        {...field}
        type="hidden"
        value={getOptionValue(users[0])}
      />
    </>
  );

  const NoUserLabel = () => (
    <span>
      no user is configured with can_impersonate = true
      that has access to this division, you should go setup one in kickstarter
    </span>
  );

  let UserComponent;

  if (division) {
    if (users && users.length > 1) {
      UserComponent = UserSelect;
    } else if (users && users.length === 1) {
      UserComponent = UserLabel;
    } else {
      UserComponent = NoUserLabel;
    }
  } else {
    UserComponent = (<div>pick a division first</div>);
  }

  if (division && UserComponent) {
    return (<UserComponent />);
  }

  return (<div />);
};

UserField.propTypes = {
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
};

const transformOption = v => ({
  label: v,
  value: v,
});

const ReasonField = ({
  name,
  ...rest
}) => {
  const props = {
    name,
    ...rest,
  };

  const { values, setFieldValue } = useFormikContext();
  const [field] = useField(props);
  const reasonTypes = ['configuration', 'troubleshooting', 'performance', 'other'];
  const { reasonType } = values;
  useEffect(() => {
    if (reasonType !== 'other') {
      setFieldValue(name, reasonType);
    }
  }, [reasonType]);

  return (
    <>
      <Field
        name="reasonType"
        type="radio-group"
        options={reasonTypes.map(transformOption)}
      />
      {values.reasonType === 'other' && (
        <Field
          {...props}
          {...field}
        />
      )}
      { values.reasonType && values.reasonType !== 'other' && (
        <Field
          {...props}
          {...field}
          type="hidden"
          value={`${values.reasonType}`}
        />
      )}
    </>
  );
};

ReasonField.propTypes = {
  name: PropTypes.string.isRequired,
};

function hasPermission(current, permissionName) {
  return !!find(current.attributes.permissions, {
    securityPermissionName: permissionName,
  });
}

const KSImpersonation = () => {
  const [divisions, setDivisions] = useState();
  const { current } = useAuth();
  const [isImpersonator, setIsImpersonator] = useState();

  useEffect(() => {
    if (current) {
      setIsImpersonator(hasPermission(current, 'canImpersonate'));
    } else {
      setIsImpersonator(false);
    }
  }, [current]);

  useEffect(() => {
    if (isImpersonator) {
      sessionInstance.get('division', {
        params: {
          page: 1,
          'page-size': 10000,
          sort: 'org-name,division-name',
        },
      }).then((r) => {
        setDivisions(r.data.data);
      }).catch((e) => {
        console.log(e.response.data.message);
      });
    } else {
      setDivisions([]);
    }
  }, [isImpersonator]);

  const onSubmit = (values, { setErrors, setSubmitting }) => {
    const payload = {
      'division-uid': values.division.id,
      'user-uid': values.user.id,
      reason: values.reason,
    };
    setSubmitting(true);
    sessionInstance.post('grant-token', payload).then((r) => {
      setAccessToken(r.data.data.attributes.accessToken);

      // now that the access token is updated, refresh to re-run the gatekeep function which will
      // redirect you to the appropriate division
      window.location.reload();
    }).catch((e) => {
      setSubmitting(false);
      setErrors(e.response.data.message);
    });
  };

  const divisionLabel = (o) => {
    if (o.attributes.orgName) {
      return `${o.attributes.orgName} - ${o.attributes.divisionName}`;
    }
    return o.attributes.divisionName;
  };

  const formBody = ({
    isSubmitting,
    values,
  }) => (
    <Card>
      <CardBody>
        <p style={{
          fontWeight: 500,
          lineHeight: 1.2,
          fontSize: '1.5rem',
        }}
        >
          Login to a division (KS Users Only)
        </p>

        <fieldset>
          <Field
            name="division"
            label="Division"
            type="select"
            options={divisions}
            getOptionLabel={divisionLabel}
            getOptionValue={o => o.id}
          />

          {values.division && (
            <UserField
              name="user"
              label="User"
              getOptionLabel={o => o.attributes.userName}
              getOptionValue={o => o.id}
            />
          )}

          {values.division && (
            <ReasonField
              name="reason"
              label="Reason"
            />
          )}

          {values.division && values.user && values.reason && (
            <Button type="submit" disabled={isSubmitting}>
              Submit{isSubmitting && 'ing'}
            </Button>
          )}
        </fieldset>
      </CardBody>
    </Card>
  );

  formBody.propTypes = {
    values: PropTypes.shape({}).isRequired,
    isSubmitting: PropTypes.bool.isRequired,
  };

  /* eslint-enable */
  const initialValues = {
    division: null,
    user: null,
    reason: null,
  };

  const TheForm = () => (
    <Form
      displayName="Login to Division (KS Users and Cordata Employees only)"
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {formBody}
    </Form>
  );

  return (
    <div>
      { isImpersonator && <TheForm />}
    </div>
  );
};

export default KSImpersonation;
